import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withPanelStyle, Text } from '@appcues/component-library';
import { Icon, Tooltip } from 'next/components/ui';
import { showModal } from 'actions/currentModal';
import { selectAccountMeta } from 'reducers/account/meta';
import { selectAccountStats } from 'reducers/account/stats';
import { CLICKED_INTEGRATION } from 'constants/events';
import {
  CONFIRM_REMOVE_INTEGRATION_MODAL,
  CONFIRM_ACTIVATE_INTEGRATION_MODAL,
  INSTALL_TO_INTEGRATE_MODAL,
  ZAPIER_INTEGRATION_MODAL,
  SALESFORCE_BETA_INTEGRATION_MODAL,
  HUBSPOT_TWO_WAY_MODAL,
  ZENDESK_SUPPORT_TWO_WAY_MODAL,
  SLACK_TWO_WAY_MODAL,
  ZENDESK_SELL_TWO_WAY_MODAL,
  TRAY_INTEGRATION_MODAL,
} from 'constants/globals';
import { trackEvent } from 'actions/events';
import betaFlag from 'assets/images/beta-flag.svg';
import { selectIsInstalled } from 'reducers/account/installedDomains';

import IntegrationBeacon from 'components/IntegrationsPage/IntegrationBeacon';

const CardFooter = styled.div`
  position: absolute;
  bottom: 0;
  padding: 15px;
  left: 0;

  ${({ available }) => !available && `opacity: 0.5;`}
`;

const CardAction = styled.div`
  opacity: 0;
  pointer-events: none;
  position: absolute;
  right: 8px;
  top: 8px;
  transition: opacity 0.2s ease;
`;

const BetaFlag = styled.img`
  left: 10px;
  max-width: 40px;
  opacity: 0.8;
  pointer-events: none;
  position: absolute;
  top: 10px;
  transition: opacity 0.2s ease;
`;

export const IntegrationCardWrapper = withPanelStyle(styled.div`
  position: relative;
  height: 120px;
  padding: ${props => (props.padding ? props.padding : '20px 40px')};
  border: 2px solid transparent;
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  color: ${props => props.theme['$green-dark']};

  &:hover {
    border-color: ${props => props.theme.$green};
    box-shadow: ${props => props.theme['$box-shadow-2']};

    & ${CardAction}, ${BetaFlag} {
      opacity: 1;
      pointer-events: all;
    }
  }

  ${({ available, isEnabled, theme }) => {
    switch (true) {
      case !available:
        return `filter: grayscale(100%);`;
      case isEnabled:
        return `
          border-color: ${theme.$green};
          box-shadow: ${theme['$box-shadow-2']};
          color: ${theme['$gray-5']};

          &:hover {
            border-color: ${theme.$red};
          }
        `;
      default:
        return ``;
    }
  }}
`);

export const IntegrationImage = styled.img`
  height: 90%;
  padding-top: 10px;
  padding-bottom: 10px;
  margin-bottom: 5px;

  ${({ available }) => !available && `opacity: 0.5;`}
`;

function IntegrationCard({
  actions,
  integration = { id: '', name: '', base64Image: '' },
  isEnabled = false,
  isInstalled,
  mau,
  meta,
}) {
  const { available, base64Image, beta, hideCardAction, id, isTwoWay, name } =
    integration;
  const trackIntegrationClickEvent = modal => {
    const isCustomer = !!meta.fusebillSubscriptionId;

    actions.trackEvent(CLICKED_INTEGRATION, {
      isInstalled,
      stripePlanId: meta.stripePlanId,
      stripeSubCreatedAt: meta.stripeSubCreatedAt,
      mau,
      id: meta.id,
      firstEmbedTimestamp: meta.firstEmbedTimestamp,
      isCustomer,
      integration: name,
      modal,
    });
  };

  const onClickIntegrationCard = () => {
    if (!available) {
      return;
    }

    if (!isInstalled) {
      actions.showModal(INSTALL_TO_INTEGRATE_MODAL);
      trackIntegrationClickEvent(INSTALL_TO_INTEGRATE_MODAL);
    } else if (name === 'Zapier') {
      actions.showModal(ZAPIER_INTEGRATION_MODAL, { integration });
      trackIntegrationClickEvent(ZAPIER_INTEGRATION_MODAL);
    } else if (name === 'Salesforce') {
      actions.showModal(SALESFORCE_BETA_INTEGRATION_MODAL, { integration });
      trackIntegrationClickEvent(SALESFORCE_BETA_INTEGRATION_MODAL);
    } else if (id === 'hubspot-two-way-integration') {
      actions.showModal(HUBSPOT_TWO_WAY_MODAL, { integration });
      trackIntegrationClickEvent(HUBSPOT_TWO_WAY_MODAL);
    } else if (id === 'zendesk-support-two-way-integration') {
      actions.showModal(ZENDESK_SUPPORT_TWO_WAY_MODAL, { integration });
      trackIntegrationClickEvent(ZENDESK_SUPPORT_TWO_WAY_MODAL);
    } else if (id === 'zendesk-sell-two-way-integration') {
      actions.showModal(ZENDESK_SELL_TWO_WAY_MODAL, { integration });
      trackIntegrationClickEvent(ZENDESK_SELL_TWO_WAY_MODAL);
    } else if (id === 'slack-integration') {
      actions.showModal(SLACK_TWO_WAY_MODAL, { integration });
      trackIntegrationClickEvent(SLACK_TWO_WAY_MODAL);
      // hopefully in the near future we can group by tray instead of specific ids
    } else if (id === 'marketo') {
      actions.showModal(TRAY_INTEGRATION_MODAL, { integration });
      trackIntegrationClickEvent(TRAY_INTEGRATION_MODAL);
    } else if (!isEnabled) {
      actions.showModal(CONFIRM_ACTIVATE_INTEGRATION_MODAL, { integration });
      trackIntegrationClickEvent(CONFIRM_ACTIVATE_INTEGRATION_MODAL);
    } else {
      actions.showModal(CONFIRM_REMOVE_INTEGRATION_MODAL, { integration });
      trackIntegrationClickEvent(CONFIRM_REMOVE_INTEGRATION_MODAL);
    }
  };

  // This ID is going to be used for CTT
  const cardId = `card-${id}-${available ? 'enabled' : 'disabled'}`;

  return (
    <Tooltip
      disabled={available}
      label={
        <>
          <IntegrationBeacon integration={name} light>
            Upgrade
          </IntegrationBeacon>{' '}
          to get access to this integration.
        </>
      }
      persist
      size="m"
    >
      <IntegrationCardWrapper
        id={cardId}
        available={available}
        isEnabled={isEnabled}
        onClick={onClickIntegrationCard}
      >
        {!hideCardAction && (
          <CardAction>
            <Icon icon={isEnabled ? 'times' : 'plus'} />
          </CardAction>
        )}
        {beta && <BetaFlag alt="Beta feature" src={betaFlag} />}
        <IntegrationImage
          available={available}
          src={`data:image/png;base64, ${base64Image}`}
        />
        {isTwoWay && (
          <CardFooter available={available}>
            <Icon icon="exchange-alt" /> <Text>Two-way</Text>
          </CardFooter>
        )}
      </IntegrationCardWrapper>
    </Tooltip>
  );
}

IntegrationCard.propTypes = {
  integration: PropTypes.shape({
    available: PropTypes.bool,
    base64Image: PropTypes.string.isRequired,
    beta: PropTypes.bool,
    hideCardAction: PropTypes.bool,
    id: PropTypes.string.isRequired,
    isTwoWay: PropTypes.bool,
    name: PropTypes.string.isRequired,
  }),
  isEnabled: PropTypes.bool,
};

const mapStateToProps = state => {
  const meta = selectAccountMeta(state);
  const accountStats = selectAccountStats(state);
  const isInstalled = selectIsInstalled(state);
  const mau =
    accountStats && accountStats.monthlyActiveUsers
      ? accountStats.monthlyActiveUsers
      : 0;

  return {
    meta,
    mau,
    isInstalled,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      showModal,
      trackEvent,
    },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationCard);
